/**
 * Shared camera pose capture + settle-guaranteed flight grammar (#252).
 *
 * Both operator camera tours — the #179 photoreal flythrough and the #180 AAR
 * replay — capture the operator's prior view on entry and restore it on exit.
 * The original idiom had two real-Chrome defects (tracker row #252, P1):
 *
 *   1. CAPTURE read `camera.position`, which is *reference-frame-relative*:
 *      under a non-identity `camera.transform` (any lookAt/viewBoundingSphere
 *      style view) it is a local-frame vector, and Cesium's flight tween also
 *      mutates it in place mid-flight. Restoring it as a world-fixed flyTo
 *      destination produced the QA garbage poses (lat π° over the Pacific,
 *      ground level in the Arctic). `positionWC` is the world-fixed spelling,
 *      and the heading/pitch/roll getters already normalise through the ENU
 *      frame at `positionWC`, so they were never poisoned.
 *
 *   2. The wall-clock fallback resolved each leg at duration+margin even while
 *      the real flight was still mid-air (Cesium tweens only advance on
 *      rendered frames; a janky, occluded, or headless tab starves them). The
 *      next leg's `flyTo` then *cancelled* the live flight mid-arc — Cesium's
 *      flyTo calls cancelFlight() internally — so orbit legs took off from
 *      mid-air ("space/black" frames) and the exit restored to wherever the
 *      cascade stranded the camera.
 *
 * Why headless QA shipped this green: the unit fakes recorded flyTo arguments
 * and asserted captured-value equality, and the headless verification stubbed
 * the clock — no real frame timing ever raced the wall-clock fallback, and a
 * fake camera has no transform for `position` to diverge under.
 *
 * The settle grammar closes both holes: `flyToSettled` resolves on the real
 * complete/cancel, and if the backstop timer fires while the flight is still
 * pending it *lands the flight deterministically* — cancelFlight() + setView()
 * to the exact destination/orientation — instead of abandoning it mid-arc.
 * Worst case in a healthy tab is a ≤ margin-sized snap onto the pose the
 * flight was already headed for; in a starved tab the tour degrades to exact
 * keyframe stills and the exit restore is always exact. `setView` destinations
 * are world-fixed regardless of the active transform (setView3D re-expresses
 * the prior transform around the new world pose), so no transform clearing is
 * needed or wanted here.
 */
import type * as Cesium from 'cesium';

/** The camera slice this module drives — structural so unit tests run on a
 *  fake (no WebGL). The real Cesium.Camera satisfies it. */
export interface SettleCamera {
  /** World-fixed camera position (NOT the frame-relative `position`). */
  positionWC: { clone(): Cesium.Cartesian3 };
  heading: number;
  pitch: number;
  roll: number;
  flyTo(options: {
    destination: unknown;
    orientation?: { heading: number; pitch: number; roll: number };
    duration?: number;
    complete?: () => void;
    cancel?: () => void;
  }): void;
  cancelFlight(): void;
  /** Loosely typed so the real Cesium.Camera (optional destination, wider
   *  orientation union) stays structurally assignable. */
  setView(options: { destination?: unknown; orientation?: unknown }): void;
}

/** The viewer slice `flyToSettled` needs. */
export interface SettleViewer {
  camera: SettleCamera;
  scene?: { requestRender?: () => void };
  isDestroyed?(): boolean;
}

/** A captured operator view, world-fixed. */
export interface CameraPose {
  destination: Cesium.Cartesian3;
  heading: number;
  pitch: number;
  roll: number;
}

/** Backstop margin past a flight's nominal duration before it is settled. */
export const SETTLE_MARGIN_MS = 300;

/** How a settled flight ended. */
export type FlightEnd =
  | 'completed' // Cesium finished the flight at its destination
  | 'settled' // backstop landed it: cancelFlight + setView(destination)
  | 'cancelled'; // an external action (operator input / another flight) won

/**
 * Capture the operator's current view in world coordinates.
 *
 * Cancels any in-flight tween first: the tour's own first flyTo would cancel
 * it one tick later anyway (Cesium flyTo calls cancelFlight internally), and
 * pinning the camera *before* reading the pose means the capture matches what
 * the operator is looking at instead of racing the tween.
 */
export function captureCameraPose(camera: SettleCamera): CameraPose {
  camera.cancelFlight();
  return {
    destination: camera.positionWC.clone(),
    heading: camera.heading,
    pitch: camera.pitch,
    roll: camera.roll,
  };
}

/**
 * Fly the camera and resolve when the flight has *deterministically ended*:
 *
 *   - 'completed': Cesium finished it (healthy render loop — the normal case);
 *   - 'settled': the backstop timer fired while the flight was still pending
 *     (janky/throttled/headless tab) — the flight is cancelled and the camera
 *     setView'd to the exact destination, so the end pose is always the
 *     intended pose;
 *   - 'cancelled': something external interrupted it (operator input or
 *     another camera move) — the interrupter owns the camera; no snap.
 */
export function flyToSettled(
  viewer: SettleViewer,
  options: {
    destination: unknown;
    orientation?: { heading: number; pitch: number; roll: number };
  },
  durationS: number,
): Promise<FlightEnd> {
  return new Promise<FlightEnd>((resolve) => {
    let done = false;
    let settling = false;
    const finish = (how: FlightEnd): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(how);
    };
    const timer = setTimeout(() => {
      if (done) return;
      if (!viewer.isDestroyed?.()) {
        settling = true; // our own cancelFlight fires the cancel callback below
        viewer.camera.cancelFlight();
        viewer.camera.setView({
          destination: options.destination,
          orientation: options.orientation,
        });
        viewer.scene?.requestRender?.();
      }
      finish('settled');
    }, durationS * 1000 + SETTLE_MARGIN_MS);
    viewer.camera.flyTo({
      ...options,
      duration: durationS,
      complete: () => finish('completed'),
      cancel: () => {
        if (!settling) finish('cancelled');
      },
    });
  });
}
